// =============================================================================
// BENEFITS SECTION COMPONENT
// =============================================================================
// A visually distinct benefits component with modern slider functionality

import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ContentComponent } from '@/types';

interface BenefitsSectionProps {
  component: ContentComponent;
}

interface BenefitItem {
  title: string;
  description: string;
  icon?: string;
  highlight?: boolean;
  conditions?: string[];
}

/**
 * Benefits Section Component
 * A modern, visually distinct component with slider functionality for showcasing benefits
 */
export const BenefitsSection: React.FC<BenefitsSectionProps> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const benefits: BenefitItem[] = metadata?.benefits || [];
  const backgroundColor = metadata?.backgroundColor || 'var(--color-bg-secondary)';
  const textColor = metadata?.textColor || 'var(--color-text-primary)';
  
  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  
  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || benefits.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % benefits.length);
    }, 5000); // Change slide every 5 seconds
    
    return () => clearInterval(interval);
  }, [isAutoPlaying, benefits.length]);
  
  // Navigation functions
  const goToSlide = (slideIndex: number) => {
    setCurrentSlide(slideIndex);
    setIsAutoPlaying(false); // Stop auto-play when user interacts
  };
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % benefits.length);
    setIsAutoPlaying(false);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + benefits.length) % benefits.length);
    setIsAutoPlaying(false);
  };
  
  // Touch event handlers for mobile swipe support
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };
  
  if (benefits.length === 0) {
    return null;
  }
  
  return (
    <section 
      id={id} 
      className="content-section benefits-section benefits-section--slider"
      style={{ 
        backgroundColor,
        color: textColor
      }}
    >
      <div className="container">
        {/* Section Header */}
        <div className="benefits-header">
          {title && <h2 className="benefits-title">{title}</h2>}
          {content && <p className="benefits-description">{content}</p>}
        </div>
        
        {/* Slider Container */}
        <div 
          className="benefits-slider"
          ref={sliderRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Navigation Arrows */}
          {benefits.length > 1 && (
            <>
              <button 
                className="slider-nav slider-nav--prev"
                onClick={prevSlide}
                aria-label="Previous benefit"
              >
                <ChevronLeft size={24} />
              </button>
              <button 
                className="slider-nav slider-nav--next"
                onClick={nextSlide}
                aria-label="Next benefit"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
          
          {/* Slider Track */}
          <div className="benefits-slider__track">
            <div 
              className="benefits-slider__slides"
              style={{ 
                transform: `translateX(-${currentSlide * 100}%)`
              }}
            >
              {benefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className={`benefits-slider__slide ${index === currentSlide ? 'benefits-slider__slide--active' : ''}`}
                >
                  <div className={`benefit-card benefit-card--slider ${benefit.highlight ? 'benefit-card--highlight' : ''}`}>
                    <div className="benefit-card__content">
                      {benefit.icon && (
                        <div className="benefit-icon">
                          <i className={benefit.icon}></i>
                        </div>
                      )}
                      
                      <div className="benefit-text">
                        <h3 className="benefit-title">{benefit.title}</h3>
                        <p className="benefit-description">{benefit.description}</p>
                        
                        {/* Conditions Badges */}
                        {benefit.conditions && benefit.conditions.length > 0 && (
                          <div className="benefit-conditions">
                            {benefit.conditions.map((condition, conditionIndex) => (
                              <span key={conditionIndex} className="condition-badge">
                                {condition}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Decorative element */}
                    <div className="benefit-decoration"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Slider Dots */}
          {benefits.length > 1 && (
            <div className="slider-dots">
              {benefits.map((_, index) => (
                <button
                  key={index}
                  className={`slider-dot ${index === currentSlide ? 'slider-dot--active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Bottom CTA */}
        {metadata?.buttons && metadata.buttons.length > 0 && (
          <div className="benefits-cta">
            {metadata.buttons.map((button, index) => (
              <a
                key={index}
                href={button.url}
                className={`btn btn--${button.variant || 'primary'} btn--${button.size || 'md'}`}
                onClick={(e) => {
                  if (button.action === 'scroll' && button.target) {
                    e.preventDefault();
                    const target = document.getElementById(button.target);
                    if (target) {
                      target.scrollIntoView({ behavior: 'smooth' });
                    }
                  }
                }}
              >
                {button.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BenefitsSection;
